import Button from 'components/button';
import Input from 'components/input';
import global from 'global';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import request from 'request';
import { useDispatch, useSelector } from 'store/hooks';
import prettierNumber from 'utils/prettier-number';
import styles from './scoring.module.scss';
import { districtsCollateral } from 'utils/constants';
import ResultSection from './result-section';
import { transcriptCollateral } from 'utils/transcript';

const ScoringCollateral = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const contextApplication = useSelector((state) => state.applicationCollateral);
  const [microdata, setMicrodata] = useState<global.macrodata>();
  const [pending, setPending] = useState(false);

  const optionInitialState = {
    key: '',
    value: '',
  };

  const [formData, setFormData] = useState({
    age: '',
    dependents: '',
    creditHistoryCount: '',
    district: optionInitialState,
    gender: optionInitialState,
    clientType: optionInitialState,
    workPlace: optionInitialState,
    maritalStatus: optionInitialState,
    education: optionInitialState,
    monthlyIncome: '',
    workExperience: '',
  });

  const [validation, setValidation] = useState<{ [key: string]: string }>({});

  const validationText = 'Это поле обязательно';

  useEffect(() => {
    request
      .get('/admin/constant_values')
      .then((res) => res.data)
      .then((d) => setMicrodata(d));
  }, []);

  useEffect(() => {
    if (!isNaN(Number(id))) return;
    toast.error('Заявка с такими данными не найдена');
    navigate('/collateral');
  }, [id]);

  const validateInputs = () => {
    const validationErrors: { [key: string]: string } = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (typeof value === 'string' && !value.trim()) {
        validationErrors[key] = validationText;
      } else if (typeof value === 'object' && !value.value.trim()) {
        validationErrors[key] = validationText;
      }
    });
    setValidation(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleCalculate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateInputs()) return;
    if (isNaN(Number(id))) return;

    setPending(true);
    request
      .post(`/applications_gold/${id}/scoring`, {
        age: Number(formData.age),
        credit_history_count: Number(formData.creditHistoryCount),
        dependants: Number(formData.dependents),
        district: formData.district.key,
        gender: formData.gender.key,
        client_type: formData.clientType.key,
        work_place: formData.workPlace.key,
        threshold: microdata?.threshold,
        marital_status: formData.maritalStatus.key,
        education: formData.education.key,
        monthly_income: Number(formData.monthlyIncome),
        work_experience: Number(formData.workExperience),
      })
      .then((res) => res.data)
      .then(() => {
        setTimeout(() => dispatch.applicationCollateral.FETCH_APPLICATION_BY_ID(id), 1000);
      })
      .catch(() => {
        toast.error('Заявка с такими данными не найдена');
      })
      .finally(() => setPending(false));
  };

  const handleClear = () => {
    setFormData({
      age: '',
      dependents: '',
      creditHistoryCount: '',
      monthlyIncome: '',
      workExperience: '',
      district: optionInitialState,
      gender: optionInitialState,
      clientType: optionInitialState,
      workPlace: optionInitialState,
      maritalStatus: optionInitialState,
      education: optionInitialState,
    });
  };

  return (
    <div className={styles.home}>
      <form className={styles.fields} onSubmit={handleCalculate}>
        <div className={styles.creditDataBox}>
          <div className={styles.title}>Данные кредита</div>
          <div className={styles.list}>
            <div className={styles.item}>
              <div className={styles.key}>ИНН</div>
              <div className={styles.value}>{contextApplication.data?.inn}</div>
            </div>

            <div className={styles.item}>
              <div className={styles.key}>Запрашиваемая сумма</div>
              <div className={styles.value}>
                {prettierNumber(contextApplication.data?.requested_amount ?? 0)}{' '}
                {transcriptCollateral.currency.getValueFromKey(
                  contextApplication.data?.currency ?? '',
                )}
              </div>
            </div>

            <div className={styles.item}>
              <div className={styles.key}>Длительность</div>
              <div className={styles.value}>{contextApplication.data?.duration} мес.</div>
            </div>

            <div className={styles.item}>
              <div className={styles.key}>Процентная ставка</div>
              <div className={styles.value}>
                {prettierNumber(contextApplication.data?.int_rate ?? 0)}%
              </div>
            </div>

            {contextApplication.data?.scoring_gold_log && (
              <div className={styles.item}>
                <div className={styles.key}>Тип клиента</div>
                <div className={styles.value}>
                  {transcriptCollateral.clientType.getValueFromKey(
                    contextApplication.data?.scoring_gold_log.client_type,
                  )}
                </div>
              </div>
            )}

            {!!contextApplication.data?.scoring_gold_log && (
              <>
                <div className={styles.item}>
                  <div className={styles.key}>Количество кредитных историй</div>
                  <div className={styles.value}>
                    {contextApplication.data.scoring_gold_log.credit_history_count}
                  </div>
                </div>
              </>
            )}

            <div className={styles.item}>
              <div className={styles.key}>Порог</div>
              <div className={styles.value}>
                {contextApplication.data?.scoring_gold_log?.threshold
                  ? `${(contextApplication.data?.scoring_gold_log?.threshold * 100).toFixed()}%`
                  : microdata?.threshold
                  ? `${Math.round(microdata?.threshold * 100)}%`
                  : '...'}
              </div>
            </div>
          </div>
        </div>

        {!!contextApplication.data?.scoring_gold_log && (
          <>
            <div className={styles.creditDataBox}>
              <div className={styles.title}>Данные клиента</div>
              <div className={styles.list}>
                <div className={styles.item}>
                  <div className={styles.key}>Возраст</div>
                  <div className={styles.value}>{contextApplication.data.scoring_gold_log.age}</div>
                </div>

                <div className={styles.item}>
                  <div className={styles.key}>Кол-во иждивенцев </div>
                  <div className={styles.value}>
                    {contextApplication.data.scoring_gold_log.dependants}
                  </div>
                </div>

                <div className={styles.item}>
                  <div className={styles.key}>Город/Район</div>
                  <div className={styles.value}>
                    {transcriptCollateral.district.getValueFromKey(
                      contextApplication?.data?.scoring_gold_log.district,
                    )}
                  </div>
                </div>

                <div className={styles.item}>
                  <div className={styles.key}>Пол</div>
                  <div className={styles.value}>
                    {transcriptCollateral.gender.getValueFromKey(
                      contextApplication.data.scoring_gold_log.gender,
                    )}
                  </div>
                </div>

                <div className={styles.item}>
                  <div className={styles.key}>Место работы клиента</div>
                  <div className={styles.value}>
                    {transcriptCollateral.workPlace.getValueFromKey(
                      contextApplication.data?.scoring_gold_log?.work_place,
                    )}
                  </div>
                </div>

                <div className={styles.item}>
                  <div className={styles.key}>Месячный доход клиента</div>
                  <div className={styles.value}>
                    {prettierNumber(contextApplication.data?.scoring_gold_log?.monthly_income)}
                  </div>
                </div>

                <div className={styles.item}>
                  <div className={styles.key}>Опыт работы (в месяцах)</div>
                  <div className={styles.value}>
                    {prettierNumber(contextApplication.data?.scoring_gold_log?.work_experience)}
                  </div>
                </div>

                <div className={styles.item}>
                  <div className={styles.key}>Семейное положение</div>
                  <div className={styles.value}>
                    {transcriptCollateral.maritalStatus.getValueFromKey(
                      contextApplication.data.scoring_gold_log.marital_status,
                    )}
                  </div>
                </div>

                <div className={styles.item}>
                  <div className={styles.key}>Образование</div>
                  <div className={styles.value}>
                    {transcriptCollateral.education.getValueFromKey(
                      contextApplication.data.scoring_gold_log.education,
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {!contextApplication.data?.scoring_gold_log && (
          <>
            <div className={styles.fieldGroup}>
              <div className={styles.title_for_section}>Данные клиента</div>
              <div className={styles.fieldList}>
                <Input
                  title="Возраст"
                  value={formData.age}
                  min={18}
                  max={80}
                  step={1}
                  placeholder={'мин: 18 макс: 80'}
                  type="integer"
                  onInput={(value) => setFormData((prev) => ({ ...prev, age: value }))}
                  error={validation?.age}
                />
                <Input
                  title="Пол"
                  id="gender"
                  value={formData.gender.key}
                  type="select"
                  options={[
                    {
                      key: 'female',
                      value: 'Женский',
                    },
                    {
                      key: 'male',
                      value: 'Мужской',
                    },
                  ]}
                  onSelect={(option) => setFormData((prev) => ({ ...prev, gender: option }))}
                  error={validation?.gender}
                />
                <Input
                  title="Семейное положение"
                  id="maritalStatus"
                  value={formData.maritalStatus.key}
                  type="select"
                  options={[
                    {
                      key: 'single',
                      value: 'Холост/Не замужем',
                    },
                    {
                      key: 'married',
                      value: 'Женат/Замужем',
                    },
                    {
                      key: 'widow_widower',
                      value: 'Вдовец/Вдова',
                    },
                    {
                      key: 'divorced',
                      value: 'Разведен(-а)',
                    },
                  ]}
                  error={validation?.maritalStatus}
                  onSelect={(option) => setFormData((prev) => ({ ...prev, maritalStatus: option }))}
                />
                <Input
                  title="Кол-во иждивенцев"
                  value={formData.dependents}
                  step={1}
                  min={0}
                  max={20}
                  placeholder={'мин: 0 макс: 20'}
                  type="integer"
                  onInput={(value) => setFormData((prev) => ({ ...prev, dependents: value }))}
                  error={validation?.dependents}
                />
                <Input
                  title="Образование"
                  id="education"
                  value={formData.education.key}
                  type="select"
                  options={[
                    {
                      key: 'higher_education',
                      value: 'Высшее',
                    },
                    {
                      key: 'incomplete_secondary_education',
                      value: 'Неполное среднее',
                    },
                    {
                      key: 'secondary_education',
                      value: 'Среднее',
                    },
                    {
                      key: 'secondary_education_with_specialisation',
                      value: 'Среднее специальное',
                    },
                    {
                      key: 'other',
                      value: 'Другое',
                    },
                  ]}
                  error={validation?.education}
                  onSelect={(option) => setFormData((prev) => ({ ...prev, education: option }))}
                />
                <Input
                  title="Месячный доход клиента"
                  value={formData.monthlyIncome}
                  step={100}
                  min={500}
                  max={2147483647}
                  placeholder={'мин: 500'}
                  type="float"
                  onInput={(value) => setFormData((prev) => ({ ...prev, monthlyIncome: value }))}
                  error={validation?.monthlyIncome}
                />
                <Input
                  title="Количество кредитных историй"
                  value={formData.creditHistoryCount}
                  min={0}
                  max={2147483647}
                  step={1}
                  placeholder={'мин: 0'}
                  type="integer"
                  onInput={(value) =>
                    setFormData((prev) => ({ ...prev, creditHistoryCount: value }))
                  }
                  error={validation?.creditHistoryCount}
                />

                <Input
                  title="Место работы клиента"
                  id="work_place"
                  value={formData.workPlace.key}
                  type="select"
                  options={[
                    {
                      key: 'governmental_employee',
                      value: 'Государственное учреждение',
                    },
                    {
                      key: 'corporate_employee',
                      value: 'Корпоративный сотрудник',
                    },
                    {
                      key: 'non_governmental_organization_employee',
                      value: 'Сотрудник НПО',
                    },
                    {
                      key: 'farmer_peasant',
                      value: 'Фермер',
                    },
                    {
                      key: 'private_organization_employee',
                      value: 'Частная компания',
                    },
                    {
                      key: 'individual_entrepreneur',
                      value: 'Индивидуальный предприниматель',
                    },
                  ]}
                  error={validation?.workPlace}
                  onSelect={(option) => setFormData((prev) => ({ ...prev, workPlace: option }))}
                />

                <Input
                  title="Опыт работы (в месяцах)"
                  value={formData.workExperience}
                  step={1}
                  min={0}
                  max={600}
                  placeholder={'мин: 0 макс: 600'}
                  type="integer"
                  onInput={(value) => setFormData((prev) => ({ ...prev, workExperience: value }))}
                  error={validation?.workExperience}
                />

                <Input
                  title="Город/Район"
                  id="district"
                  value={formData.district.key}
                  type="select"
                  options={districtsCollateral}
                  hasFilterText={districtsCollateral.length > 10}
                  onSelect={(option) => setFormData((prev) => ({ ...prev, district: option }))}
                  error={validation?.district}
                />
                <Input
                  title="Тип клиента"
                  type="select"
                  value={formData.clientType.key}
                  options={[
                    {
                      key: 'old',
                      value: 'Повторный',
                    },
                    {
                      key: 'new',
                      value: 'Новый',
                    },
                  ]}
                  onSelect={(option) => setFormData((prev) => ({ ...prev, clientType: option }))}
                  error={validation?.clientType}
                />
              </div>
            </div>

            <div className={styles.footerBox}>
              <div className={styles.buttonGroup}>
                <Button type="submit" pending={pending} disabled={pending}>
                  Рассчитать
                </Button>
                <Button onClick={handleClear} pending={pending} color="red">
                  Очистить
                </Button>
              </div>
            </div>
          </>
        )}
      </form>
      <ResultSection contextApplication={contextApplication} pending={pending} />
    </div>
  );
};

export default ScoringCollateral;
